import React from 'react';
import { Mission, User } from '../../types';
import { sanitize } from '../../lib/security';

interface BountyCardProps {
  mission: Mission;
  poster?: User;
  onClick?: () => void;
}

const STATUS_CLASS: Record<string, string> = {
  open: 'status-open', active: 'status-active',
  started: 'status-started', done: 'status-done', cancelled: 'status-cancelled',
};

const STATUS_LABEL: Record<string, string> = {
  open: 'OPEN', active: 'ACTIVE', started: 'IN PROGRESS', done: 'COMPLETE', cancelled: 'CANCELLED',
};

export const BountyCard: React.FC<BountyCardProps> = ({ mission, poster, onClick }) => {
  const platformFee = (mission.reward * 0.07).toFixed(2);

  return (
    <article
      className="bounty-card"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => { if (onClick && (e.key === 'Enter' || e.key === ' ')) onClick(); }}
      aria-label={`Mission: ${mission.title}, Reward: €${mission.reward}`}
    >
      {/* Dashed inner border via ::before in CSS */}
      <div className="bounty-card-inner">
        <div className="bounty-header">
          <div className="poster-deco">
            <span className="star-deco">★</span> BOUNTY POSTED <span className="star-deco">★</span>
          </div>
          <h3 className="bounty-title">{sanitize(mission.title)}</h3>
        </div>

        <div className="bounty-reward">
          <span className="reward-amount">€{mission.reward}</span>
          <span className="reward-fee">+€{platformFee} fee</span>
        </div>

        <div className="bounty-meta">
          <span>⏱ {mission.estimatedHours}h</span>
          <span>📍 {sanitize(mission.location || 'Remote')}</span>
          <span>📂 {sanitize(mission.category)}</span>
        </div>

        <p className="bounty-desc">{sanitize(mission.description)}</p>

        <div className="bounty-skills">
          {mission.skills.slice(0, 5).map((s) => (
            <span key={s} className="skill-tag">{sanitize(s)}</span>
          ))}
        </div>

        <div className="bounty-footer">
          <span className="bounty-poster">
            By {poster ? `${sanitize(poster.firstName)} ${sanitize(poster.lastName)}` : '?'}
          </span>
          <span className={`status-badge ${STATUS_CLASS[mission.status] ?? 'status-open'}`}>
            {STATUS_LABEL[mission.status] ?? mission.status.toUpperCase()}
          </span>
        </div>
      </div>
    </article>
  );
};
